import { omit } from 'lodash';

import { Exploration, ExplorationTab } from '@/explore/types';
import { generateShortId } from '@/lib/utils/string';

import { setExplorationCells } from '.';
import { removeViewOptions } from '../cell/view-options';
import { deleteCell } from '../cell/cells';

export const getExplorationTabs = (exploration: Exploration): ExplorationTab[] => {
  return exploration.view.tabs?.groups.at(0)?.tabs ?? [];
};

export const getExplorationTab = (
  exploration: Exploration,
  tabId: string,
): ExplorationTab | undefined => {
  const tabs = getExplorationTabs(exploration);
  return tabs.find((tab) => tab.tabId === tabId);
};

export const setExplorationTabs = (
  exploration: Exploration,
  tabs: ExplorationTab[],
): Exploration => {
  if (tabs.length === 0) {
    return {
      ...exploration,
      view: omit(exploration.view, 'tabs'),
    };
  }

  return {
    ...exploration,
    view: {
      ...exploration.view,
      tabs: {
        groups: [{ tabs }],
      },
    },
  };
};

export const getCellTabId = (exploration: Exploration, cellId: string): string | undefined => {
  const tabs = getExplorationTabs(exploration);
  if (tabs.length === 0) {
    return undefined;
  }

  const tab = tabs.find((t) => t.cellIds.includes(cellId));
  return tab?.tabId ?? undefined;
};

export const setCellTabId = (
  exploration: Exploration,
  cellId: string,
  tabId: string | null,
): Exploration => {
  const tabs = getExplorationTabs(exploration);
  if (tabs.length === 0) {
    return exploration;
  }

  const updatedTabs = tabs.map((tab) => {
    const filteredCellIds = tab.cellIds.filter((id) => id !== cellId);
    if (tab.tabId === tabId) {
      return { ...tab, cellIds: [...filteredCellIds, cellId] };
    }
    return { ...tab, cellIds: filteredCellIds };
  });

  return setExplorationTabs(exploration, updatedTabs);
};

export const getExplorationCellsByTab = (exploration: Exploration, tabId: string) => {
  const tabs = getExplorationTabs(exploration);
  if (tabs.every((tab) => tab.tabId !== tabId)) {
    return [];
  }

  const cellIds = tabs.find((tab) => tab.tabId === tabId)?.cellIds ?? [];
  return exploration.view.cells.filter((cell) => cellIds.includes(cell.id) ?? false);
};

export const addExporationTab = (exploration: Exploration, tab: ExplorationTab) => {
  const tabs = getExplorationTabs(exploration);
  const explorationHasTabs = tabs.length > 0;

  // If user is creating their first tab, create another default tab with all existing cells
  const updatedTabs = explorationHasTabs
    ? [...tabs, tab]
    : [
        {
          tabId: generateShortId(),
          label: 'Tab 1',
          cellIds: exploration.view.cells.map((cell) => cell.id),
        },
        tab,
      ];

  return setExplorationTabs(exploration, updatedTabs);
};

export const deleteExplorationTab = (exploration: Exploration, tabId: string) => {
  const tabs = getExplorationTabs(exploration);
  if (tabs.length === 0) {
    return exploration;
  }

  const cellIdsToRemove = getExplorationCellsByTab(exploration, tabId).map((cell) => cell.id);

  const updatedCells = cellIdsToRemove.reduce(
    (cells, cellId) => deleteCell(cells, cellId),
    exploration.view.cells,
  );

  return setExplorationCells(
    setExplorationTabs(
      exploration,
      tabs.filter((tab) => tab.tabId !== tabId),
    ),
    updatedCells,
  );
};

export const moveExplorationTab = (
  exploration: Exploration,
  fromIndex: number,
  toIndex: number,
  after: boolean,
) => {
  const tabs = getExplorationTabs(exploration);
  if (tabs.length === 0) {
    return exploration;
  }

  const newTabs = [...tabs];
  const [movedTab] = newTabs.splice(fromIndex, 1);
  const targetIndex = after ? toIndex + 1 : toIndex;
  newTabs.splice(targetIndex - (toIndex > fromIndex ? 1 : 0), 0, movedTab);

  return setExplorationTabs(exploration, newTabs);
};

export const moveCellsToTab = (
  exploration: Exploration,
  cellIds: string[],
  tabId: string | null,
) => {
  const tabs = getExplorationTabs(exploration);
  if (tabId !== null && tabs.length > 0 && !tabs.some((tab) => tab.tabId === tabId)) {
    throw new Error(`Tab with id ${tabId} does not exist in exploration`);
  }
  const updatedTabs = tabs.map((tab) => {
    const filteredCellIds = tab.cellIds.filter((id) => !cellIds.includes(id));
    if (tab.tabId === tabId) {
      return { ...tab, cellIds: [...filteredCellIds, ...cellIds] };
    }
    return { ...tab, cellIds: filteredCellIds };
  });

  const movedCells = exploration.view.cells
    .filter((cell) => cellIds.includes(cell.id))
    .map((cell) => {
      return removeViewOptions(cell, ['rowId']);
    });

  const updatedCells = [
    ...movedCells,
    ...exploration.view.cells.filter((cell) => !cellIds.includes(cell.id)),
  ];

  return setExplorationTabs(
    {
      ...exploration,
      view: {
        ...exploration.view,
        cells: updatedCells,
      },
    },
    updatedTabs,
  );
};

export const ensureCellTabs = (exploration: Exploration, activeTabId: string | null) => {
  const tabs = getExplorationTabs(exploration);
  if (tabs.length === 0 || activeTabId === null) {
    return exploration;
  }

  const cellsInTabs = tabs.flatMap((tab) => tab.cellIds);
  const cellsWithNoTab = exploration.view.cells.filter((cell) => !cellsInTabs.includes(cell.id));

  if (cellsWithNoTab.length === 0) {
    return exploration;
  }

  const updatedTabs = tabs.map((tab) => {
    if (tab.tabId === activeTabId) {
      return {
        ...tab,
        cellIds: [...tab.cellIds, ...cellsWithNoTab.map((cell) => cell.id)],
      };
    }
    return tab;
  });
  return setExplorationTabs(exploration, updatedTabs);
};

export const getNewTabs = (exploration1: Exploration, exploration2: Exploration) => {
  const tabs1 = getExplorationTabs(exploration1);
  const tabs2 = getExplorationTabs(exploration2);
  return tabs2.filter((tab2) => tabs1.every((tab1) => tab1.tabId !== tab2.tabId));
};
